import type { LocationEditSchema } from '@repo/schemas/location/locationEdit';
import type { LocationPublic } from '@repo/types/locations';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useEditLocation } from '@repo/api/location/useEditLocation';
import { useGetLocation } from '@repo/api/location/useGetLocation';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { locationEditSchema } from '@repo/schemas/location/locationEdit';
import { CheckCircle2Icon, MapPinnedIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import LocationFormFields from '@/components/features/manage/manage-locations/LocationFormFields';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form } from '@/components/ui/form';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the EditLocation component.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 * @property {LocationPublic} [location] - The location to edit.
 */
interface EditLocationProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  location?: LocationPublic;
}

export default function EditLocation(editLocationPropsProps: EditLocationProps) {
  const { open, onOpenChange, location } = editLocationPropsProps;
  const getLocationQuery = useGetLocation(location?.id);

  const editLocationMutation = useEditLocation();

  const form = useForm<LocationEditSchema>({
    defaultValues: {
      name: '',
      desc: '',
      coordinates: '',
      address: null,
    },
    mode: 'onBlur',
    resolver: standardSchemaResolver(locationEditSchema),
  });

  const onSubmit = (data: LocationEditSchema) => {
    editLocationMutation
      .mutateAsync(data)
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  const onClose = () => {
    onOpenChange(false);
    form.reset();
  };

  const handleOnOpenChange = (value: boolean) => {
    if (!value) {
      onClose();
    } else {
      onOpenChange(true);
    }
  };

  useEffect(() => {
    if (getLocationQuery.data) {
      const parsed = locationEditSchema.safeParse(getLocationQuery.data);
      if (parsed.success) {
        form.reset(parsed.data);
      }
    }
  }, [getLocationQuery.data, form]);

  useEffect(() => {
    if (getLocationQuery.isError) {
      toasterMessageHandler(baseErrorMessages, getLocationQuery.error);
      onClose();
    }
  }, [getLocationQuery.isError, getLocationQuery.error, onClose]);

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogContent className={'md:max-w-148'}>
        <Form {...form}>
          <DialogBody>
            <LoadingOverlay isLoading={getLocationQuery.isPending}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa ładowanie danych...</LoadingOverlayLabel>
            </LoadingOverlay>
            <LoadingOverlay isLoading={editLocationMutation.isPending}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa edycja danych...</LoadingOverlayLabel>
            </LoadingOverlay>
            <DialogHeader className={'border-border-primary bg-card items-center gap-2 border-b'}>
              <FeaturedIcon size={'lg'}>
                <MapPinnedIcon />
              </FeaturedIcon>
              <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                <DialogTitle>Edytuj lokalizację</DialogTitle>
                <DialogDescription className={'text-muted-foreground text-xs'}>Wprowadź nazwę i współrzędne lokacji.</DialogDescription>
              </div>
            </DialogHeader>
            <form id={'edit-location-form'} className={'flex grow flex-col gap-3 overflow-auto p-4'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <LocationFormFields />
            </form>
          </DialogBody>
          <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
            <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>
            <Button disabled={editLocationMutation.isPending} form={'edit-location-form'} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <LoadingSwap isLoading={editLocationMutation.isPending} className="flex items-center gap-2">
                <CheckCircle2Icon />
                Zapisz
              </LoadingSwap>
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
